import { computed, inject, Injectable, signal } from '@angular/core';
import { map, Observable } from 'rxjs';
import { DevicesDataService } from 'src/app/services/data/devices-data/devices-data.service';
import { TAB_VALUE } from 'src/app/shared/components/listing-page/listing-page.types';
import {
  chartHealthSegmentsColors,
  createDeviceBadgesFn
} from 'src/app/shared/components/listing-page/services/listing-page-model/listing-page-model.constants';
import {
  ListingDataSource,
  ListingPageModel
} from 'src/app/shared/components/listing-page/services/listing-page-model/listing-page-model.types';
import { ListingTableComponent } from 'src/app/shared/components/listing-table/listing-table.component';
import { DeviceSidePanelTitleComponent } from '../../components/device-side-panel-title/device-side-panel-title.component';
import { DeviceSidePanelComponent } from '../../components/device-side-panel/device-side-panel.component';
import { DeviceListingFieldName, DevicesPayload, OrderDirection, TableDevices } from '../../components/devices.models';
import { TranslateRegistry } from 'src/app/services/translate/translate.registry';
import { ExportRow } from '@portal/shared/types/export.types';
import { ListingPageModelAbstract } from 'src/app/shared/components/listing-page/services/listing-page-model/listing-page-model-abstract';
import { DevicesMapComponent } from '@portal/shared/components/map-report-group/map-report-group.component';
import { ListingPageContext } from '@portal/shared/components/listing-page/services/listing-page-model/listing-page-context.types';
import { Applicable } from 'src/app/pages/administration/report-groups/report-groups.types';
import { devicesListingSource } from '../../constants/devices.constants';
import { ListingPageId } from '@portal/shared/components/listing-page/store/listing-page-store.types';
import { ListingPageModelRegistry } from '@portal/shared/components/listing-page/services/listing-page-model/listing-page-model-registry.service';
import { MeteringListingFiltersSourceService } from './metering-listing-filters-source.service';
import { TimezoneService } from 'src/app/services/utility/timezone.service';
import { Store } from '@ngrx/store';
import { isSingleCommodityClient } from 'src/app/pages/help-and-settings/user-settings/store/user-settings.reducer';
import { createMeteringColumns } from './metering-listing.columns';
import { mapMeteringMeterToExport } from './metering-listing.export';
import { METERING_DEFAULT_SEARCH_TYPE, METERING_SEARCH_TYPES, METERING_SORT_FIELD } from './metering-listing.search';

@Injectable()
export class MeteringListingModelService extends ListingPageModelAbstract implements ListingPageModel {
  private readonly devices = inject(DevicesDataService);
  private readonly timezoneService = inject(TimezoneService);
  readonly listingPageModelRegistry = inject(ListingPageModelRegistry);
  readonly #isSingleCommodityClient = inject(Store).selectSignal(isSingleCommodityClient);

  readonly pageTitle = TranslateRegistry.get.METERING.TITLE.METERING;
  readonly searchTypeList = signal(METERING_SEARCH_TYPES);
  readonly selectedSearchType = signal(METERING_DEFAULT_SEARCH_TYPE);
  readonly sorting = signal({
    columnName: DeviceListingFieldName.LastHeard,
    orderDirection: OrderDirection.DESC
  });
  readonly tableColumns = computed(() =>
    createMeteringColumns({ isSingleCommodityClient: this.#isSingleCommodityClient() })
  );
  readonly dataSource: ListingDataSource<TableDevices> = {
    getAll: tableState => {
      return this.devices.prepareDataForTable(tableState);
    },
    total: 0
  };
  readonly exportConfig = {
    dataSource: {
      export: (payload: Pick<DevicesPayload, 'filters' | 'sort'>): Observable<ExportRow[]> => {
        return this.devices
          .export(payload)
          .pipe(
            map(response =>
              response.map(meter => mapMeteringMeterToExport(meter, d => this.timezoneService.formatLong(d)))
            )
          );
      }
    },
    fileName: 'Export-Metering'
  };
  readonly filterSource = inject(MeteringListingFiltersSourceService);

  readonly selectionFieldName = 'deviceUID';
  readonly chartSegmentsColors = chartHealthSegmentsColors;
  readonly createBadgesFn = createDeviceBadgesFn;
  readonly mapCmp = DevicesMapComponent;
  readonly tableCmp = ListingTableComponent;
  readonly contentClasses: string[] = ['listing-page__content_breakpoint'];
  readonly defaultTab = TAB_VALUE.Table;
  readonly sortField = METERING_SORT_FIELD;
  readonly sidePanelTitleCmp = DeviceSidePanelTitleComponent;
  readonly sidePanelBodyCmp = DeviceSidePanelComponent;
  readonly createMapCmpInputs = (ctx: ListingPageContext): Record<string, unknown> => {
    return {
      selectedLocationData: ctx.selectedLocation(),
      filtersPayload: ctx.mapFilters(),
      chartSegmentsColors: this.chartSegmentsColors,
      createBadgesFn: this.createBadgesFn,
      locationSelectedFn: ctx.onLocationSelected,
      applicable: Applicable.AmiHealthDashboard,
      actionSource: devicesListingSource,
      pageId: this.pageId
    };
  };
  get pageId(): ListingPageId {
    return ListingPageId.Metering;
  }

  constructor() {
    super();
    const registry = this.listingPageModelRegistry;
    registry.add(ListingPageId.Metering, this);
  }
}
